import type {
  CurrencyCode,
  GlycerinCartProduct,
  GlycerinService,
  GlycerinWishListProduct,
} from '@packages/tracking/src/types/types';
import { GlycerinAvailability } from '@packages/tracking/src/types/enums';
import type {
  LineItemFragmentFragment,
  WishlistLineItemsFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { getFragmentData } from '@packages/gql/generated/shopping';

type Contained<T> = T extends Array<infer C> ? C : never;

const makeArticleId = (sku: string): string => {
  let articleId = '';
  if (sku) {
    if (sku.indexOf('-') === -1) {
      articleId = sku;
    } else {
      articleId = sku.substring(0, sku.indexOf('-'));
    }
  }
  if (articleId.trim().length === 0) {
    articleId = 'Unknown';
  }
  return articleId;
};

export const buildGlycerinCartProduct = (
  lineItem: LineItemFragmentFragment,
): GlycerinCartProduct => {
  const aplusFragment = getFragmentData(AplusFragmentFragmentDoc, lineItem.aplus);
  let availability: GlycerinAvailability;
  switch (lineItem.availability.state) {
    case 'NOT_AVAILABLE':
      availability = GlycerinAvailability.UNAVAILABLE;
      break;
    case 'NOT_YET_AVAILABLE':
      availability = GlycerinAvailability.NOTIMMEDIATELY;
      break;
    case 'AVAILABLE':
    default:
      availability = GlycerinAvailability.IMMEDIATELY;
  }
  return {
    productId: lineItem.product.akl || 'Unknown',
    articleId: makeArticleId(lineItem.product.sku),
    skuId: lineItem.product.sku || 'Unknown',
    quantity: lineItem.quantity || 1,
    availability,
    currency: lineItem.price.currency as CurrencyCode,
    displayedPrice: lineItem.price.value,
    originalPrice: lineItem.price.valueOld || undefined,
    // services: [],
    services: aplusFragment?.clusters
      ?.map((aplusCluster) =>
        aplusCluster.services.map<GlycerinService>(
          (aplus): Contained<GlycerinCartProduct['services']> => ({
            serviceType: aplusCluster.label,
            name: aplus.name || 'Unknown',
            quantity: aplus.selected || aplus.included ? 1 : 0,
            displayedPrice: aplus.price?.value || 0,
            currency: aplus.price?.currency as unknown as CurrencyCode,
            serviceId: aplus.uuid || 'Unknown',
          }),
        ),
      )
      .reduce((p, n) => [...p, ...n], []),
    /*
    Needed later while checkout implementation
    vouchers: lineItem.coupons?.map<GlycerinVoucher>((coupon) => ({
      voucherType: 'Default',
      name: coupon.couponName || 'Unknown',
      value: coupon.credit?.value || 0,
      currency: coupon.credit?.currency as CurrencyCode,
    })),
    */
  };
};

export const buildGlycerinWishlistProduct = (
  product: WishlistLineItemsFragmentFragment,
): GlycerinWishListProduct => {
  const { sku, price, akl, availability } = product;
  let availabilityGlycerin: GlycerinAvailability;
  switch (availability.state) {
    case 'NOT_AVAILABLE':
      availabilityGlycerin = GlycerinAvailability.UNAVAILABLE;
      break;
    case 'NOT_YET_AVAILABLE':
      availabilityGlycerin = GlycerinAvailability.NOTIMMEDIATELY;
      break;
    case 'AVAILABLE':
      availabilityGlycerin = GlycerinAvailability.IMMEDIATELY;
      break;
    default:
      availabilityGlycerin = GlycerinAvailability.UNKNOWN;
  }
  return {
    productId: akl,
    articleId: makeArticleId(sku),
    skuId: sku,
    availability: availabilityGlycerin,
    currency: price?.currency ? (price.currency as CurrencyCode) : 'EUR',
    displayedPrice: price?.value || 0,
    originalPrice: price?.valueOld && price.valueOld > 0 ? price.valueOld : undefined,
  } satisfies GlycerinWishListProduct;
};

export const buildGlycerinWishlistProducts = (
  products?: WishlistLineItemsFragmentFragment[] | null | undefined,
): GlycerinWishListProduct[] => products?.map((item) => buildGlycerinWishlistProduct(item)) || [];

/* To be continued for payments while checkout implementation */
